'use client'

import { ChevronDownIcon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useMemo } from 'react'

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { getPrecisionFromStep } from '@/features/order/lib/number-format-util'
import {
  OrderFieldDivider,
  OrderFieldStepper,
} from '@/features/order/order-form/components/widget/order-field-stepper'
import {
  AnchorPrice,
  computeAnchorPrice,
  computeEstimatedPrice,
  OffsetUnit,
  PriceAnchor,
} from '@/features/order/order-form/lib/resolve-anchor-price'
import { useNumberInput } from '@/hooks/use-number-format'
import { cn, safeParseFloat } from '@/lib/utils'

export type { OffsetUnit, PriceAnchor }

interface LimitPriceFieldProps {
  anchor: PriceAnchor
  onAnchorChange: (anchor: PriceAnchor) => void
  offsetUnit: OffsetUnit
  onOffsetUnitChange: (unit: OffsetUnit) => void
  value: string
  onValueChange: (val: string) => void
  onReset: () => void
  onSwitchToPrice?: () => void
  step?: number
  quoteCurrency: string
  disableAbsolutePrice?: boolean
  disableQuoteValueUnit?: boolean
  tickerData?: AnchorPrice
  tickSize?: number
}

// The anchor / unit selects: the same cell as the amount field's unit (InputWithTickerList).
const UNIT_TRIGGER_CLASS =
  'text-foreground/60 hover:bg-foreground/5 hover:text-foreground flex h-full w-auto shrink-0 cursor-pointer items-center gap-1 rounded-none border-none bg-transparent px-3 text-sm font-medium transition-colors'

const OFFSET_LIMITS: Partial<Record<OffsetUnit, { min: number; max: number }>> = {
  percent: { min: -50, max: 100 },
  bps: { min: -5000, max: 10000 },
}

/**
 * Convert an offset value from one unit to another.
 * Uses anchorPrice as the reference: compute absoluteDelta first, then re-express in newUnit.
 *
 * quote_value : absoluteDelta = offset
 * percent     : absoluteDelta = anchorPrice × (offset / 100)
 * bps         : absoluteDelta = anchorPrice × (offset / 10000)
 * ticks       : absoluteDelta = offset × tickSize
 */
function convertOffset(
  currentOffset: number,
  fromUnit: OffsetUnit,
  toUnit: OffsetUnit,
  anchorPrice: number,
  tickSize: number
): number {
  if (fromUnit === toUnit) return currentOffset

  // Step 1 → absolute delta in quote-currency terms
  let absoluteDelta: number
  if (fromUnit === 'quote_value') absoluteDelta = currentOffset
  else if (fromUnit === 'percent') absoluteDelta = anchorPrice * (currentOffset / 100)
  else if (fromUnit === 'bps') absoluteDelta = anchorPrice * (currentOffset / 10000)
  else absoluteDelta = currentOffset * tickSize // ticks

  // Step 2 → express in target unit
  if (toUnit === 'quote_value') return absoluteDelta
  if (toUnit === 'percent') return anchorPrice > 0 ? (absoluteDelta / anchorPrice) * 100 : 0
  if (toUnit === 'bps') return anchorPrice > 0 ? (absoluteDelta / anchorPrice) * 10000 : 0
  return tickSize > 0 ? Math.round(absoluteDelta / tickSize) : 0 // ticks — integer
}

/** Reasonable decimal places per unit */
function decimalsForUnit(unit: OffsetUnit): number {
  if (unit === 'quote_value') return 2
  if (unit === 'percent') return 2
  if (unit === 'bps') return 1
  return 0 // ticks — integers only
}

function decimalsForOffsetUnit(unit: OffsetUnit, priceDecimals: number): number {
  if (unit === 'quote_value') return priceDecimals
  return decimalsForUnit(unit)
}

/** Convert an absolute price to an offset relative to anchorPrice in the given unit */
function computeOffsetFromAbsolute(
  absolutePrice: number,
  anchorPrice: number,
  unit: OffsetUnit,
  tickSize: number
): number {
  const delta = absolutePrice - anchorPrice
  if (unit === 'quote_value') return delta
  if (unit === 'percent') return anchorPrice > 0 ? (delta / anchorPrice) * 100 : 0
  if (unit === 'bps') return anchorPrice > 0 ? (delta / anchorPrice) * 10000 : 0
  return tickSize > 0 ? Math.round(delta / tickSize) : 0 // ticks — integer
}

export function LimitPriceField({
  anchor,
  onAnchorChange,
  offsetUnit,
  onOffsetUnitChange,
  value,
  onValueChange,
  onReset,
  onSwitchToPrice,
  step,
  quoteCurrency,
  disableAbsolutePrice = false,
  disableQuoteValueUnit = false,
  tickerData,
  tickSize,
}: LimitPriceFieldProps) {
  const t = useTranslations('orderForm')
  const isRelative = anchor !== 'price'
  const anchorLabel = (value: PriceAnchor) => t(`priceAnchor.${value}`)

  const decimals = useMemo(() => {
    return getPrecisionFromStep(step)
  }, [step])

  const offsetDecimals = useMemo(
    () => decimalsForOffsetUnit(offsetUnit, decimals),
    [decimals, offsetUnit]
  )

  const placeholder = (0).toFixed(isRelative ? offsetDecimals : decimals)

  // Typed in the user's separator style; only a relative offset may be negative.
  const numberInput = useNumberInput(value, { allowNegative: isRelative })

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = numberInput.parse(e.target.value)
    if (val !== null) onValueChange(val)
  }

  const handleBlur = () => {
    const num = safeParseFloat(value)
    if (isNaN(num)) return

    const limits = OFFSET_LIMITS[offsetUnit]
    if (limits && isRelative) {
      const clamped = Math.min(Math.max(num, limits.min), limits.max)
      onValueChange(clamped.toFixed(offsetDecimals))
      return
    }

    if (step && step > 0 && (!isRelative || offsetUnit === 'quote_value')) {
      onValueChange((Math.round(num / step) * step).toFixed(isRelative ? offsetDecimals : decimals))
    } else {
      onValueChange(num.toFixed(isRelative ? offsetDecimals : decimals))
    }
  }

  const effectiveStep = useMemo(() => {
    if (!isRelative) return step ?? 1
    if (offsetUnit === 'bps') return 1
    if (offsetUnit === 'percent') return 0.01
    if (offsetUnit === 'ticks') return 1
    return step ?? 1 // quote_value: price precision
  }, [isRelative, offsetUnit, step])

  const handleIncrease = () => {
    const current = safeParseFloat(value) || 0
    let next = current + effectiveStep
    const limits = OFFSET_LIMITS[offsetUnit]
    if (limits && isRelative) next = Math.min(next, limits.max)
    onValueChange(next.toFixed(isRelative ? offsetDecimals : decimals))
  }

  const handleDecrease = () => {
    const current = safeParseFloat(value) || 0
    let next = isRelative ? current - effectiveStep : Math.max(0, current - effectiveStep)
    const limits = OFFSET_LIMITS[offsetUnit]
    if (limits && isRelative) next = Math.max(next, limits.min)
    onValueChange(next.toFixed(isRelative ? offsetDecimals : decimals))
  }

  const handleOffsetUnitChange = (newUnit: OffsetUnit) => {
    const currentOffset = safeParseFloat(value)
    const anchorPrice = computeAnchorPrice(anchor, tickerData)
    if (!isNaN(currentOffset) && currentOffset !== 0 && anchorPrice !== null && anchorPrice > 0) {
      const converted = convertOffset(
        currentOffset,
        offsetUnit,
        newUnit,
        anchorPrice,
        tickSize ?? 1
      )
      onValueChange(converted.toFixed(decimalsForOffsetUnit(newUnit, decimals)))
    }
    onOffsetUnitChange(newUnit)
  }

  const handleAnchorChange = (newAnchor: PriceAnchor) => {
    onAnchorChange(newAnchor)

    if (newAnchor === 'price') {
      // Switch to absolute: trigger ATM sync (syncPrice fills lastPrice)
      onSwitchToPrice?.()
      return
    }

    // Switch to (or between) relative anchors: convert current value → offset for newAnchor
    const newAnchorPrice = computeAnchorPrice(newAnchor, tickerData)
    if (newAnchorPrice === null || newAnchorPrice <= 0) {
      onReset()
      return
    }

    let absolutePrice: number
    if (anchor === 'price') {
      // Current value is an absolute price
      absolutePrice = safeParseFloat(value)
    } else {
      // Current value is a relative offset — compute absolute first
      const estimated = computeEstimatedPrice(
        anchor,
        safeParseFloat(value),
        offsetUnit,
        tickerData,
        tickSize
      )
      absolutePrice = estimated ?? safeParseFloat(value)
    }

    if (isNaN(absolutePrice) || absolutePrice <= 0) {
      onReset()
      return
    }

    const offset = computeOffsetFromAbsolute(
      absolutePrice,
      newAnchorPrice,
      offsetUnit,
      tickSize ?? 1
    )
    onValueChange(offset.toFixed(offsetDecimals))
  }

  const estimatedPrice = useMemo(() => {
    if (!isRelative) return null
    const offset = safeParseFloat(value)
    if (isNaN(offset) || offset === 0) return null
    return computeEstimatedPrice(anchor, offset, offsetUnit, tickerData, tickSize)
  }, [isRelative, value, anchor, offsetUnit, tickerData, tickSize])

  const unitLabel = useMemo(() => {
    if (offsetUnit === 'quote_value') return quoteCurrency
    if (offsetUnit === 'bps') return 'BPS'
    if (offsetUnit === 'percent') return '%'
    return t('offsetUnit.ticks')
  }, [offsetUnit, quoteCurrency, t])

  return (
    <div className="flex flex-col gap-1.5 overflow-hidden">
      {/* [anchor | price | unit | +/−] — the same build as the amount field above
          (InputWithTickerList): one bordered h-9 row, 1px dividers, the shared stepper. */}
      <div className="border-foreground/10 flex h-9 items-center overflow-hidden border">
        <Select value={anchor} onValueChange={(v) => handleAnchorChange(v as PriceAnchor)}>
          <SelectTrigger hideIcon data-1p-ignore tabIndex={-1} className={UNIT_TRIGGER_CLASS}>
            <SelectValue>{anchorLabel(anchor)}</SelectValue>
            <ChevronDownIcon className="size-3 shrink-0" />
          </SelectTrigger>
          <SelectContent className="z-[60]">
            {!disableAbsolutePrice && (
              <SelectItem value="price" className="hover:bg-foreground/10 cursor-pointer text-sm">
                {anchorLabel('price')}
              </SelectItem>
            )}
            <SelectItem value="best_bid" className="hover:bg-foreground/10 cursor-pointer text-sm">
              {anchorLabel('best_bid')}
            </SelectItem>
            <SelectItem value="best_ask" className="hover:bg-foreground/10 cursor-pointer text-sm">
              {anchorLabel('best_ask')}
            </SelectItem>
            <SelectItem value="mid" className="hover:bg-foreground/10 cursor-pointer text-sm">
              {anchorLabel('mid')}
            </SelectItem>
          </SelectContent>
        </Select>
        <OrderFieldDivider />
        <input
          type="text"
          // A relative offset can be negative, and iOS's decimal pad has no minus key.
          inputMode={isRelative ? 'text' : 'decimal'}
          autoComplete="off"
          data-1p-ignore
          aria-label={isRelative ? t('limitPriceOffset') : t('limitPrice')}
          value={numberInput.display}
          onChange={handleInputChange}
          onBlur={handleBlur}
          placeholder={placeholder}
          className="text-foreground placeholder:text-foreground/20 h-full min-w-0 flex-1 bg-transparent px-3 text-right text-sm font-medium outline-none"
        />
        {isRelative && (
          <>
            <OrderFieldDivider />
            <Select
              value={offsetUnit}
              onValueChange={(v) => handleOffsetUnitChange(v as OffsetUnit)}
            >
              <SelectTrigger
                hideIcon
                data-1p-ignore
                tabIndex={-1}
                className={cn(UNIT_TRIGGER_CLASS, 'min-w-18.5 justify-center')}
              >
                <SelectValue>{unitLabel}</SelectValue>
                <ChevronDownIcon className="size-3 shrink-0" />
              </SelectTrigger>
              <SelectContent className="z-[60] min-w-18.5">
                {!disableQuoteValueUnit && (
                  <SelectItem
                    value="quote_value"
                    className="hover:bg-foreground/10 cursor-pointer text-sm"
                  >
                    {quoteCurrency}
                  </SelectItem>
                )}
                <SelectItem value="bps" className="hover:bg-foreground/10 cursor-pointer text-sm">
                  BPS
                </SelectItem>
                <SelectItem
                  value="percent"
                  className="hover:bg-foreground/10 cursor-pointer text-sm"
                >
                  %
                </SelectItem>
                <SelectItem value="ticks" className="hover:bg-foreground/10 cursor-pointer text-sm">
                  {t('offsetUnit.ticks')}
                </SelectItem>
              </SelectContent>
            </Select>
          </>
        )}
        <OrderFieldDivider />
        <OrderFieldStepper onIncrement={handleIncrease} onDecrement={handleDecrease} />
      </div>

      {/* Estimated price hint (relative anchors only) */}
      {isRelative && estimatedPrice !== null && (
        <p className="mr-1 ml-auto text-xs text-white/40">
          {t('estimatedPrice')}{' '}
          {estimatedPrice.toLocaleString('en-US', {
            maximumFractionDigits: decimals,
            minimumFractionDigits: Math.min(decimals, 2),
          })}
        </p>
      )}
    </div>
  )
}
